import React, {useEffect, useMemo} from 'react';
import {Actions} from '../../../common/actions';
import {useDispatch, useSelector} from 'react-redux';
import {hasAccessPermission} from '../../../common/permissions/validations';
import {resources, scopes} from '../../../common/permissions/dictionary';
import {Modal, Button, Grid, Popup, Icon} from 'semantic-ui-react';
import './vehiclesHistoryColor.scss';
import VehicleData from './components/vehicleData';
import {colorTypes} from '../../constants';
import MaintainanceTable from '../vehiclesPriceColor/table/table';
import {columnsColors} from './columns';

const {
  addMessageBoxError,
  fetchActiveColors,
  fetchVehiclesHistoryColor,
  updateVehiclesColor,
  createVehiclesColor,
  changeVehiclesHistoryColorState,
} = Actions;

const VehiclesHistoryColor = ({modalWasOpen, selectedVehicle, onCloseModal}) => {
  const permissions = useSelector((state) => state.session.permissions);
  const {loading, initialState,
    colorsHistory, activeColors} = useSelector((state) => state.vehicleHistoryColors);
  const data = useMemo(() => colorsHistory || [], [colorsHistory]);
  const dispatch = useDispatch();

  const fetchData = () => {
    const masterCode = (selectedVehicle && selectedVehicle.masterCode) || '';
    const sfx = (selectedVehicle && selectedVehicle.sfx) || '';
    dispatch(fetchActiveColors());
    dispatch(fetchVehiclesHistoryColor(masterCode, sfx));
  };

  useEffect(() => {
    if (!hasAccessPermission(permissions, resources.VehiclesHistoryColor, scopes.See)) {
      dispatch(addMessageBoxError('Petición no procesada, usuario no cuenta con el permiso de visualización.'));
      return;
    }
    fetchData();
  }, []);

  const onCloseModalClick = () => {
    onCloseModal();
  };

  const onSaveColor = (masterCode, sfx) => {
    if (!hasAccessPermission(permissions, resources.VehiclesHistoryColor, scopes.Create)) {
      dispatch(addMessageBoxError('Petición no procesada, usuario no cuenta con el permiso de creación.'));
      return;
    }
    const params = {
      'master': masterCode,
      'sfx': sfx,
      'detail': [
        {
          ...initialState,
        },
      ],
    };
    dispatch(createVehiclesColor(params));
  };

  const onChangeSelectState = (event, item) => {
    const status = event.target && event.target.value;
    const data = {
      'master': selectedVehicle.masterCode,
      'sfx': selectedVehicle.sfx,
      'detail': [
        {
          ...item,
          'status': status,
        },
      ],
    };
    if (!hasAccessPermission(permissions, resources.VehiclesHistoryColor, scopes.Edit)) {
      dispatch(addMessageBoxError('Petición no procesada, usuario no cuenta con el permiso de edición.'));
      return;
    }
    dispatch(updateVehiclesColor(data));
  };

  const columns = useMemo(() => columnsColors(onChangeSelectState), []);

  const getColorDescription = (value) => {
    const color = activeColors.find((obj) => {
      return obj.colorCode === value;
    });
    return (color && color.colorDescription) || '';
  };

  useEffect(() => {
    dispatch(changeVehiclesHistoryColorState('outDoorColorDescription',
      getColorDescription(initialState.outDoorColor)));
  }, [initialState.outDoorColor]);

  useEffect(() => {
    dispatch(changeVehiclesHistoryColorState('inDoorColorDescription',
      getColorDescription(initialState.inDoorColor)));
  }, [initialState.inDoorColor]);

  const renderColorOptions = (activeColors) => {
    return (
      <React.Fragment>
        {activeColors.map((item) => {
          return (
            <option
              key={item.colorCode}
              value={item.colorCode}>
              {`${item.colorCode} - ${item.colorDescription}`}
            </option>
          );
        })}
      </React.Fragment>
    );
  };

  const renderOption = (options) => {
    return (
      <React.Fragment>
        {options.map((item) => {
          return (
            <option
              key={item.key}
              value={item.value}>
              {item.text}
            </option>
          );
        })}
      </React.Fragment>
    );
  };

  const renderOutDoorColor = () => {
    const outDoorColors = activeColors.filter((obj) => {
      return obj.type === 'E';
    });
    return (
      <React.Fragment>
        <Grid.Column width={2}>
          <h4>Código Exterior:</h4>
        </Grid.Column>
        <Grid.Column width={5}>
          <select
            onChange={(event) => dispatch(changeVehiclesHistoryColorState('outDoorColor', event.target.value))}
            value={initialState.outDoorColor || ''}
          >
            <option value="">Seleccione Código Exterior</option>
            {renderColorOptions(outDoorColors)}
          </select>
        </Grid.Column>
      </React.Fragment>
    );
  };
  
  const renderInDoorColor = () => {
    const inDoorColors = activeColors.filter((obj) => {
      return obj.type === 'I';
    });
    return (
      <React.Fragment>
        <Grid.Column width={2}>
          <h4>Código Interior:</h4>
        </Grid.Column>
        <Grid.Column width={5}>
          <select
            onChange={(event) => dispatch(changeVehiclesHistoryColorState('inDoorColor', event.target.value))}
            value={initialState.inDoorColor || ''}
          >
            <option value="">Seleccione Código Interior</option>
            {renderColorOptions(inDoorColors)}
          </select>
        </Grid.Column>
      </React.Fragment>
    );
  };

  const renderColorType = () => {
    return (
      <React.Fragment>
        <Grid.Column width={2}>
          <h4>Tipo de Color:</h4>
        </Grid.Column>
        <Grid.Column width={5}>
          <select
            onChange={(event) => dispatch(changeVehiclesHistoryColorState('colorType', event.target.value))}
            value={initialState.colorType || ''}
          >
            <option value="">Seleccione Tipo de Color</option>
            {renderOption(colorTypes)}
          </select>
        </Grid.Column>
      </React.Fragment>
    );
  };

  return (
    <Modal size={'fullscreen'} className='stock-download-modal' open={modalWasOpen}>
      <Modal.Header>
        Asignar colores al vehículo: {selectedVehicle.masterCode}
        <Popup
          trigger={
            <Icon link name='info circle' color='blue' size='large' />
          }
          on='click'
          position='bottom left'
          inverted
        >
          <div>
            Pantalla de configuración de colores que será utilizado para el maestro de vehículos
          </div>
        </Popup>
      </Modal.Header>
      <Modal.Content scrolling>
        <Modal.Description>
          <div className='title-bar'>
            <div className='filters-section'>
              <Grid textAlign='left'>
                <VehicleData selectedVehicle={selectedVehicle.values || selectedVehicle}/>
                <Grid.Row>
                  {renderOutDoorColor()}
                  {renderInDoorColor()}
                  <Grid.Column width={2}>
                    <Button
                      primary
                      className='toyota-primary'
                      onClick={() => onSaveColor(selectedVehicle.masterCode, selectedVehicle.sfx)}
                      disabled={!(initialState.inDoorColor && initialState.outDoorColor && initialState.colorType)}>
                      Añadir
                    </Button>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  {renderColorType()}
                </Grid.Row>

              </Grid>
            </div>
          </div>
          <MaintainanceTable
            columns={columns}
            data={data || []}
            loading={loading}
          />
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onCloseModalClick}>
          Cancelar
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
export default VehiclesHistoryColor;
